<!-- Mobile Navigation Backdrop -->
@if (isMobile && mobileMenuOpen) {
  <div
    animate.enter="bt-fade-enter"
    animate.leave="bt-fade-leave"
    class="mobile-nav-backdrop"
    (click)="closeMobileMenu()"
    aria-hidden="true">
  </div>
}

<!-- Mobile Navigation Drawer -->
@if (isMobile && mobileMenuOpen) {
  <nav
    animate.enter="bt-mobile-slide-enter"
    animate.leave="bt-mobile-slide-leave"
    id="mobile-nav-drawer"
    class="mobile-nav-drawer"
    role="navigation"
    aria-label="Main navigation">
    <!-- Mobile Header -->
    <div class="mobile-nav-header">
      <img src="./assets/images/bt-logo.svg" alt="BindTuning" class="mobile-nav-logo">
      <button
        type="button"
        class="mobile-nav-close"
        (click)="closeMobileMenu()"
        aria-label="Close navigation menu">
        <svg width="24" height="24" aria-hidden="true">
          <use xlink:href="#icon-close"></use>
        </svg>
      </button>
    </div>
    <!-- Mobile Navigation Content -->
    <div class="mobile-nav-content">
      <!-- Main Menu (shown when no submenu is expanded) -->
      @if (!mobileSubmenuOpen) {
        <ul class="mobile-nav-list" role="menubar">
          @if (loading) {
            <li class="mobile-nav-loading">
              @for (item of [1,2,3,4,5]; track item) {
                <div class="mobile-nav-skeleton">
                  <div class="skeleton-icon"></div>
                  <div class="skeleton-text"></div>
                </div>
              }
            </li>
          } @else {
            @for (item of sideNavigationItems; track item) {
              <li
                role="none"
                animate.enter="bt-fade-enter-slow"
                animate.leave="bt-fade-leave-slow">
                <a
                  role="menuitem"
                  tabindex="0"
                  class="mobile-nav-item group"
                  [ngClass]="{
                    'is-active': item.isActive,
                    'is-disabled': item.isDisabled
                  }"
                  (click)="onMobileNavItemClick(item)"
                  (keyup.enter)="onMobileNavItemClick(item)"
                  (keyup.space)="onMobileNavItemClick(item)"
                  [attr.aria-disabled]="item.isDisabled"
                  [attr.aria-expanded]="(item.sections?.length ?? 0) > 0 ? expandedMobileItem?.title === item.title : null"
                  [attr.aria-haspopup]="(item.sections?.length ?? 0) > 0 ? 'true' : null">
                  <div class="mobile-nav-item-icon">
                    <svg width="20" height="20" class="inactive text-white/40 group-[.is-active]:opacity-0" aria-hidden="true">
                      <use [attr.xlink:href]="'#' + item.icon + '-false'"></use>
                    </svg>
                    <svg width="20" height="20" class="active opacity-0 group-[.is-active]:opacity-100" aria-hidden="true">
                      <use [attr.xlink:href]="'#' + item.icon + '-true'"></use>
                    </svg>
                  </div>
                  <span class="mobile-nav-item-label">{{ item.title }}</span>
                  @if (item.sections && item.sections.length > 0) {
                    <svg
                      width="20" height="20"
                      class="mobile-nav-item-arrow"
                      aria-hidden="true">
                      <use xlink:href="#icon-caret-down"></use>
                    </svg>
                  }
                </a>
              </li>
            }
          }
        </ul>
      }
      <!-- Submenu (shown when an item with children is expanded) -->
      @if (mobileSubmenuOpen && expandedMobileItem) {
        <div class="mobile-submenu">
          <!-- Back button -->
          <button
            type="button"
            class="mobile-submenu-back"
            (click)="mobileGoBack()"
            aria-label="Go back to main menu">
            <svg width="20" height="20" aria-hidden="true">
              <use xlink:href="#icon-arrow-left"></use>
            </svg>
            <span>Back</span>
          </button>
          <!-- Submenu header -->
          <div class="mobile-submenu-header">
            <svg width="24" height="24" aria-hidden="true">
              <use [attr.xlink:href]="'#' + expandedMobileItem.icon + '-true'"></use>
            </svg>
            <span>{{ expandedMobileItem.title }}</span>
          </div>
          <!-- Submenu items -->
          <ul class="mobile-nav-list" role="menu">
            @for (section of expandedMobileItem.sections; track section; let si = $index) {
              @for (child of section?.childs; track child) {
                <li role="none">
                  <a
                    role="menuitem"
                    tabindex="0"
                    class="mobile-nav-item mobile-submenu-item group"
							[ngClass]="{
								'is-active': child.isActive,
								'is-disabled': child.isDisabled
							}"
                    (click)="onMobileSubNavItemClick(child)"
                    (keyup.enter)="onMobileSubNavItemClick(child)"
                    (keyup.space)="onMobileSubNavItemClick(child)"
                    [attr.aria-disabled]="child.isDisabled">
                    <div class="mobile-nav-item-icon">
                      <svg width="20" height="20" class="inactive text-white/40 group-[.is-active]:opacity-0" aria-hidden="true">
                        <use [attr.xlink:href]="'#' + child.icon + '-false'"></use>
                      </svg>
                      <svg width="20" height="20" class="active opacity-0 group-[.is-active]:opacity-100" aria-hidden="true">
                        <use [attr.xlink:href]="'#' + child.icon + '-true'"></use>
                      </svg>
                    </div>
                    <span class="mobile-nav-item-label">{{ child.title }}</span>
                    @if (child.isActive) {
                      <span class="mobile-nav-active-indicator" aria-hidden="true"></span>
                    }
                  </a>
                </li>
              }
              <!-- Section separator -->
              @if (expandedMobileItem.sections && si < expandedMobileItem.sections.length - 1) {
                <li
                  class="mobile-nav-separator"
                  role="separator">
                </li>
              }
            }
          </ul>
        </div>
      }
    </div>
    <!-- Mobile Footer - Token Balance -->
    @if (isAuthenticated && tokenSummary.current !== null && !hasAllLicenses) {
      <div class="mobile-nav-footer">
        <div class="mobile-token-balance">
          <div class="mobile-token-header">
            <span class="mobile-token-label">
              <svg width="16" height="16" aria-hidden="true">
                <use xlink:href="#icon-premium"></use>
              </svg>
              Credits
            </span>
            <a href="/pricing" class="mobile-token-upgrade" target="_blank" (click)="onUpgradeClick('mobile')">Upgrade</a>
          </div>
          <span class="mobile-token-count">{{ tokenSummary.current }} of {{ tokenSummary.total }} left</span>
          <div class="mobile-token-bar">
            <div class="mobile-token-bar-fill" [style.width.%]="tokenSummary.percent"></div>
          </div>
        </div>
      </div>
    }
  </nav>
}

<!-- Desktop Sidebar Navigation (hidden on mobile) -->
<div class="desktop-sidebar-wrapper" [class.hidden-mobile]="isMobile" [style.width.px]="navigationWrapperWidth"
  [ngClass]="{'flex': isNavigationPinned}">

  <!-- SideBar Navigation -->
  <div class="main-nav group/sidebar bg-dark-gray border-r border-white/12 h-full rounded-sm transition-[width] duration-200 ease-in-out relative z-3"
    [ngClass]="{'show w-58': (sideBarNavigationOpened || isNavigationPinned || hoverSidebar) && navigationWrapperWidth > 0, 'w-64px': !sideBarNavigationOpened && !isNavigationPinned && !hoverSidebar && navigationWrapperWidth > 0, 'w-0 opacity-0': navigationWrapperWidth === 0}"
    (mouseenter)="hoverSidebar = true" (mouseleave)="hoverSidebar = false">

    <!-- BindTuning Logo -->
    <div class="flex items-center justify-center px-16px group-[.show]/sidebar:justify-between pt-24px h-32px">
      <img src="./assets/images/bt-logo.svg" alt="BindTuning"
        class="group-[.show]/sidebar:w-38.5 group-[.show]/sidebar:opacity-100 w-0px h-32px transition-[width] duration-200 ease-in-out object-cover object-left">

        @if (subMenuItem) {
          <button type="button" aria-expanded="true" aria-controls="sidebar" data-toggle data-target="#sidebar"
            class="flex btn-icon text-mid-gray-60" (click)="pinNavigation()">
            <svg width="16" height="16" [ngClass]="{'hidden': isNavigationPinned, 'block': !isNavigationPinned}">
              <use xlink:href="#icon-side-panel-true"></use>
            </svg>
            <svg width="16" height="16" [ngClass]="{'block': isNavigationPinned, 'hidden': !isNavigationPinned}">
              <use xlink:href="#icon-side-panel-false"></use>
            </svg>
          </button>
        }
      </div>

      <!-- Navigation nodes -->
      <nav class="px-16px py-24px relative">
        @if (loading) {
          <ul>
            @for (item of [1,2,3,4,5]; track item; let i = $index) {
              <li>
                <div class="flex">
                  <div class="my-8px ml-8px min-w-4 min-h-4 rounded-sm bg-white/40 animate-pulse"></div>
                  <div
                    class="my-8px ml-8px w-0 h-16px rounded-sm bg-white/40 animate-pulse group-[.show]/sidebar:w-37.5 group-[.show]/sidebar:opacity-100 opacity-0 transition-all duration-300 ease-in-out">
                  </div>
                </div>
              </li>
            }
          </ul>
        } @else {
          <ul>
            <!-- Loop through side navigation items -->
            @for (item of sideNavigationItems; track item; let i = $index) {
              <li [class.active]="item.isActive"
                animate.enter="bt-fade-enter-slow"
                animate.leave="bt-fade-leave-slow">
                <a (click)="navigateMenuItem(item)" tabindex="0" (keyup)="navigateMenuItem(item)"
                  class="flex group items-start py-8px pl-8px rounded-sm leading-4 font-medium hover:bg-cta-dark hover:rounded-r-none active:text-cta text-14 relative"
						data-collapse attr.data-target="#submenu-{{i}}" [ngClass]="{
							'is-submenu-opened': subMenuItem && subMenuItem.title === item.title,
							'is-active text-cta': item.isActive,
							'opacity-40 cursor-not-allowed': item.isDisabled,
						}">
                  <div class="relative">
                    <svg width="16" height="16"
                      class="text-white/40 block relative z-0 group-[.is-active]:opacity-0">
                      <use [attr.xlink:href]="'#' + item.icon + '-false'"></use>
                    </svg>
                    <svg width="16" height="16"
                      class="block absolute z-0 inset-s-0 top-0 opacity-0 group-[.is-active]:opacity-100">
                      <use [attr.xlink:href]="'#' + item.icon + '-true'"></use>
                    </svg>
                  </div>
                  <span
                    class="ml-8px pe-8px group-[.show]/sidebar:w-auto group-[.show]/sidebar:opacity-100 w-0 opacity-0 transition-all duration-300 ease-in-out flex whitespace-nowrap">{{
                    item.title }}
                  </span>
                  @if (item.sections && item.sections.length > 0) {
                    <svg width="16" height="16"
                      class="ms-auto rotate-270 shrink-0 opacity-0 group-[.show]/sidebar:opacity-100 transition-all duration-300 ease-in-out text-white/60 group-[.is-active]:text-cta"
                      [ngClass]="{'opacity-40 cursor-not-allowed': item.isDisabled}">
                      <use xlink:href="#icon-caret-down"></use>
                    </svg>
                  }
                </a>
              </li>
            }
          </ul>
        }
      </nav>

      <!-- token balance template (reused) -->
      <ng-template #tokenBalanceTemplate let-containerClass="containerClass" let-maxWidth="maxWidth">
        <div [ngClass]="containerClass || 'absolute left-0 bottom-0 mx-16px mb-16px bg-gray-heavy rounded-md p-12px z-1 overflow-hidden group-[.show]/sidebar:w-50 group-[.show]/sidebar:opacity-100 opacity-0'"
          [style.maxWidth.px]="maxWidth" style="transition: ease-in-out, opacity 100ms ease-in-out; z-index:1;">
          <div class="flex flex-col justify-between gap-16px">
            <div class="flex justify-between gap-16px">
              <span class="font-text font-medium text-14 leading-5 flex items-center">
                <svg width="16" height="16" class="mr-4px">
                  <use xlink:href="#icon-premium"></use>
                </svg>
                Credits
              </span>

              <div class="flex flex-col gap-8px">
                <a href="/pricing" class="font-text font-medium text-14 leading-4"
                target="_blank" (click)="onUpgradeClick('desktop')">Upgrade</a>
              </div>
            </div>

            @if (tokenSummary) {
              <span class="font-text font-medium text-14 leading-5 text-grey-CC">{{ tokenSummary.current ??
              '-' }} of {{ tokenSummary.total ?? '-' }} left</span>
            } @else {
              <span class="font-text font-medium text-14 leading-5 text-grey-CC">-</span>
            }
          </div>
          <span class="w-full absolute left-0 bottom-0 h-0.5 bg-green-gray-10"></span>
          <span class="absolute left-0 bottom-0 h-0.5 bg-primary" [style.width.%]="tokenSummary.percent"></span>
        </div>
      </ng-template>
      @if (isAuthenticated && tokenSummary.current !== null && !hasAllLicenses) {
        <ng-container
        *ngTemplateOutlet="tokenBalanceTemplate; context: { containerClass: 'absolute left-0 bottom-0 mx-16px mb-16px bg-gray-heavy rounded-md p-12px z-1 overflow-hidden group-[.show]/sidebar:w-50 group-[.show]/sidebar:opacity-100 opacity-0', maxWidth: (sideBarNavigationOpened || isNavigationPinned || (hoverSidebar && navigationWrapperWidth > 0)) ? 220 : 0 }"></ng-container>
      }


      <!-- License Notifications (main sidebar) -->
      @if (isAuthenticated && licenseNotifications.length > 0) {
        <div class="absolute left-0 mx-16px w-full flex flex-col gap-8px overflow-hidden group-[.show]/sidebar:w-50 group-[.show]/sidebar:opacity-100 opacity-0"
          [style.bottom.px]="isAuthenticated && tokenSummary.current !== null && !hasAllLicenses ? 100 : 16"
          [style.maxWidth.px]="(sideBarNavigationOpened || isNavigationPinned || (hoverSidebar && navigationWrapperWidth > 0)) ? 200 : 0"
          style="transition: max-width 200ms ease-in-out, opacity 100ms ease-in-out;"
          >
          <!-- Loading state while activating trial -->
          @if (isActivatingTrial) {
            <div class="bg-gray-heavy rounded-md p-12px">
              <div class="flex items-center gap-8px">
                <div class="loader-circle h-16px w-16px"></div>
                <span class="font-text text-14 leading-5">Activating trial...</span>
              </div>
            </div>
          }
          @if (!isActivatingTrial) {
            @for (notification of licenseNotifications; track notification) {
              <button (click)="goToSubscriptionsPage()">
                <bt-message animate.enter="bt-slide-up-enter" animate.leave="bt-slide-up-leave"
                  [title]="notification.title ?? ''" [message]="notification.message" [allowDismiss]="false"
                  [type]="notification.type" [icon]="notification.type" [layout]="'Normal-No-Border'"
                  [theme]="'dark'" [allowDismiss]="notification.dismiss" [action]="notification.action">
                </bt-message>
              </button>
            }
          }
        </div>
      }

    </div>

    <!-- Submenu for navigation items -->
    <div class="submenu bg-dark-gray border-r border-white/12 h-full w-58 group/sidebar-submenu transition-all duration-300 ease-in-out absolute top-0 left-64px z-2"
      [ngClass]="{'show w-58 opacity-100': !loading && subMenuItem, 'w-0 opacity-0': !loading && !subMenuItem, 'absolute': !isNavigationPinned }">

      <div class="flex gap-8px py-20px px-16px">
        <svg width="24" height="24" class="text-white block relative z-0 group-[.is-active]:opacity-0">
          <use [attr.xlink:href]="'#' + subMenuItem?.icon + '-false'"></use>
        </svg>
        <p class="font-display font-medium text-20 leading-6">{{subMenuItem?.title}}</p>

      </div>

      <nav class="px-8px relative">
        @if (subMenuItem) {
          <ul>
            <!-- Loop through sections -->
            @for (section of subMenuItem.sections; track section; let si = $index) {
              <!-- Loop through side navigation items -->
              @for (child of section?.childs; track child; let i = $index) {
                <li [class.active]="child.isActive">
                  <a (click)="navigateSubMenuItem(child, true)" tabindex="0"
                    (keyup)="navigateSubMenuItem(child, true)"
                    class="flex group items-center py-8px pl-8px rounded-sm leading-4 font-medium hover:bg-cta/4 active:text-cta text-14"
							data-collapse attr.data-target="#submenu-{{i}}" [ngClass]="{
													'is-active text-cta': child.isActive,
													'opacity-40 cursor-not-allowed': child.isDisabled
												}">
                    <div class="relative">
                      <svg width="16" height="16"
                        class="text-white/40 block relative z-0 group-[.is-active]:opacity-0">
                        <use [attr.xlink:href]="'#' + child.icon + '-false'"></use>
                      </svg>
                      <svg width="16" height="16"
                        class="block absolute z-0 inset-s-0 top-0 opacity-0 group-[.is-active]:opacity-100">
                        <use [attr.xlink:href]="'#' + child.icon + '-true'"></use>
                      </svg>
                    </div>
                    <span class="ml-8px pe-8px flex justify-between items-center w-full">{{ child.title }} <span
                    class="w-2px h-20px bg-primary rounded-sm opacity-0 group-[.is-active]:opacity-100"></span></span>
                  </a>
                </li>
              }
              <!-- Separator between sections -->
              @if (subMenuItem.sections && si < subMenuItem.sections.length - 1) {
                <div
                class="my-4 border-t border-mid-gray-80"></div>
              }
            }
          </ul>
        }
      </nav>

      @if (isAuthenticated) {
        <div class="absolute left-0 bottom-0 mx-16px mb-16px w-full flex flex-col gap-8px max-w-50"
          >
          <!-- Loading state while activating trial -->
          @if (isActivatingTrial) {
            <div class="bg-gray-heavy rounded-md p-12px">
              <div class="flex items-center gap-8px">
                <div class="loader-circle h-16px w-16px"></div>
                <span class="font-text text-14 leading-5">Activating trial...</span>
              </div>
            </div>
          }
          <!-- Licensing Notifications for all products -->
          @if (!isActivatingTrial) {
            @for (notification of licenseNotifications; track notification) {
              <button (click)="goToSubscriptionsPage()">
                <bt-message animate.enter="bt-slide-up-enter" animate.leave="bt-slide-up-leave" [title]="notification.title ?? ''" [message]="notification.message"
                  [allowDismiss]="false" [type]="notification.type" [icon]="notification.type"
                  [layout]="'Normal-No-Border'" [theme]="'dark'" [action]="notification.action"
                  [allowDismiss]="notification.dismiss">
                </bt-message>
              </button>
            }
          }
          <!-- Token balance (reused) -->
          @if (tokenSummary.current !== null && !hasAllLicenses) {
            <ng-container
            *ngTemplateOutlet="tokenBalanceTemplate; context: { containerClass: 'bg-gray-heavy rounded-md p-12px', maxWidth: null }"></ng-container>
          }
        </div>
      }

    </div>
  </div>
